/* eslint-disable */
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { getHigherUnitAndQuantity, getHigherUnitName } from '@/Helpers';

export const baseHeaderFields = [
	{
		id: 'planName', label: 'Plan Name', labelIcon: 'clipboard', type: 'text', required: true, placeholder: 'Enter plan name', wrapperClass: 'basis-[calc(50%-0.5rem)] !max-w-none',
	},
	{
		id: 'planningDate', label: 'Planning Date', labelIcon: 'calendar', type: 'date', shouldDisableDate: true, required: true,
	},
	{
		id: 'kitchen', label: 'Select Kitchen', labelIcon: 'kitchen', type: 'tabs', required: true, wrapperClass: 'basis-full !w-full !min-w-0',
	},
	{
		id: 'shift', label: 'Shift', labelIcon: 'clock', type: 'tabs', enableField: ['kitchen'], required: true, wrapperClass: 'basis-full !w-full !min-w-0',
	},
];

const mockInputClass = 'w-full !bg-[#fbfaf6] !border-[#e8e4da] !rounded-lg !h-10 !text-[13px] '
	+ '[&_.rs-picker-toggle]:!bg-[#fbfaf6] [&_.rs-picker-toggle]:!border-[#e8e4da] [&_.rs-picker-toggle]:!rounded-lg';

export const headerLayoutConfig = {
	containerClass: 'flex flex-row flex-wrap items-start gap-4 w-full',
	fieldWrapperClass: 'flex flex-col gap-1.5 flex-1 min-w-[180px] items-start',
	labelClass: 'text-[13px] font-semibold text-gray-700 leading-[1.2] m-0 p-0 whitespace-nowrap',
	inputClass: mockInputClass,
	tabContainerClass: 'flex gap-2 w-full',
	selectedTabClass: 'px-4 py-2 text-[12px] font-semibold border rounded-lg bg-amber-100 text-black border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.5)] whitespace-nowrap transition-all',
	unselectedTabClass: 'px-4 py-2 text-[12px] font-medium border rounded-lg bg-[#fbfaf6] text-gray-500 border-[#e8e4da] hover:bg-gray-50 hover:text-gray-700 whitespace-nowrap transition-all',
};

export const STEPS = [
	{ id: 1, title: 'Date & Shift', subtitle: 'Select plan basics' },
	{ id: 2, title: 'Stock Configuration', subtitle: 'Edit stock & review final production' },
];

export const toNum = (v) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
};

export const QTY_KEYS = ['orderedForTomorrow', 'currentStock', 'closingStock', 'plannedProduction', 'plannedFulfilled', 'plannedPending', 'dispatchQty', 'plannedConsumption', 'todayPlannedConsumption', 'threshold', 'maxStock', 'stockAtEndOfDayTomorrow', 'productionQty', 'batchSize', 'unpackedClosingStock', 'unpackedPlannedProduction', 'unpackedThreshold', 'unpackedMaxStock'];

export const buildLists = (stores, localShiftList) => ({
	kitchenList: (stores || [])?.filter((store) => store?.type === 'BASE').map((s) => ({ label: s?.name, value: s?.id })),
	shiftList: (localShiftList || []).map((s) => ({ ...s, label: s?.shiftName || s?.name, value: s?.id })),
});

export const buildHeaderFields = (lists) => baseHeaderFields.map((f) => {
	if (f.id === 'kitchen') return { ...f, tabOptions: (lists?.kitchenList || []).map((k) => k.label) };
	if (f.id === 'shift') return { ...f, tabOptions: (lists?.shiftList || []).map((s) => s.label) };
	return f;
});

export const labelToValue = (list = [], label) => list?.find((i) => i?.label === label)?.value;

export const buildVariantMetaMap = (productList) => {
	const map = {};
	(productList || []).forEach((p) => {
		(p?.variants || []).forEach((v) => {
			map[v.id] = {
				originalUnit: p?.unit,
				unit: getHigherUnitName(p?.unit),
				skuType: p?.skuType,
			};
		});
	});
	return map;
};

export const toHigherValue = (originalUnit, value) => {
	const u = String(originalUnit || '').toLowerCase().trim();
	if (!u) return value;
	const [hv] = getHigherUnitAndQuantity(u, Number(value) || 0);
	return Number(hv);
};

export const buildColumns = (planningDate) => {
	const d = moment(planningDate || new Date());
	const planDay = d.format('DD/MM/YYYY');
	const yesterday = moment().subtract(1, 'day').format('DD/MM/YYYY');
	const today = moment().format('DD/MM/YYYY');
	return [
		{ key: 'sno', title: 'S.No', width: 70, fixed: true },
		{ key: 'productName', title: 'Product Name', width: 260, sortable: true, fixed: true },
		// { key: 'orderedForTomorrow', title: 'Already Planned Order Qty', subTitle: `On ${planDay}`, note: 'Qty already ordered for delivery on this date', customCol: true, width: 200 },
		{ key: 'currentStock', title: 'Current Stock', width: 140 },
		{ key: 'closingStock', title: 'Closing Stock', subTitle: `On ${yesterday}`, customCol: true, width: 170 },
		{ key: 'plannedConsumption', title: 'Plan Consumption', width: 160 },
		{ key: 'todayPlannedConsumption', title: 'Today Planned Consumption', subTitle: `On ${today}`, customCol: true, width: 180 },
		{ key: 'stockAtEndOfDayTomorrow', title: 'Day End Closing Stock', subTitle: `On ${planDay}`, customCol: true, width: 180 },
		{ key: 'threshold', title: 'Threshold / Min Stock', customCol: true, width: 180 },
		{ key: 'maxStock', title: 'Recommend Stock', subTitle: 'For production qty if below min stock', customCol: true, width: 180 },
		{ key: 'unpackedProductName', title: 'Unpacked Product', width: 200 },
		{ key: 'unpackedClosingStock', title: 'Unpacked Closing Stock', subTitle: `On ${yesterday}`, customCol: true, width: 170 },
		{ key: 'unpackedPlannedProduction', title: 'Unpacked Planned Production', width: 180 },
		{ key: 'unpackedThreshold', title: 'Unpacked Threshold', subTitle: `On ${today}`, customCol: true, width: 160 },
		{ key: 'unpackedMaxStock', title: 'Unpacked Max Stock', width: 160 },
		{ key: 'plannedFulfilled', title: 'Planned Fulfilled', width: 150 },
		{ key: 'dispatchQty', title: 'Total Dispatch Qty', subTitle: `On ${today} & Beyond`, customCol: true, width: 160 },
		{ key: 'plannedProduction', title: 'Planned Production', subTitle: `On ${today}`, note: 'Production already planned for this date via earlier plans', customCol: true, width: 160 },
		{ key: 'batchSize', title: 'Batch Size', width: 130 },
		{ key: 'processName', title: 'Process', width: 140, fixed: true },
		{ key: 'productionQty', title: 'Final Production', width: 150 },
	];
};

// Custom column header — main title with the date as a small badge below, and an
// optional one-line explanation (note) under that.
export const renderCustomCol = (column) => (
	<div className='flex flex-col items-start gap-[3px]'>
		<span>{column?.title}</span>
		{column?.subTitle && (
			<span className='text-[10px] font-medium normal-case bg-white/70 text-gray-600 px-1.5 py-[1px] rounded whitespace-nowrap'>
				{column.subTitle}
			</span>
		)}
		{column?.note && (
			<span className='text-[10px] italic font-normal normal-case text-gray-500 whitespace-normal leading-tight text-left'>
				{column.note}
			</span>
		)}
	</div>
);
// Two-step flow: step 2 carries the full review column set (the old step-3
// columns are a superset of the old step-2 ones).
export const stepColumnKeys = {
	2: ['sno', 'productName', 'processName', 'closingStock', 'plannedProduction', 'plannedConsumption', 'todayPlannedConsumption', 'dispatchQty', 'stockAtEndOfDayTomorrow', 'batchSize', 'threshold', 'maxStock', 'unpackedProductName', 'unpackedClosingStock', 'unpackedPlannedProduction', 'unpackedThreshold', 'unpackedMaxStock', 'productionQty'],
};

export const stepColumnOrder = {
	2: ['sno', 'productName', 'processName', 'closingStock', 'plannedProduction', 'plannedConsumption', 'todayPlannedConsumption', 'dispatchQty', 'stockAtEndOfDayTomorrow', 'batchSize', 'threshold', 'maxStock', 'unpackedProductName', 'unpackedClosingStock', 'unpackedPlannedProduction', 'unpackedThreshold', 'unpackedMaxStock', 'productionQty'],
};

export const editableColumnKeys = {
	2: ['closingStock', 'threshold', 'maxStock', 'unpackedClosingStock', 'unpackedThreshold', 'unpackedMaxStock', 'productionQty'],
};


export const isClosingStockLocked = (row, thresholdOverride) => {
	const th = thresholdOverride === undefined ? row?.threshold : thresholdOverride;
	return row?.isEditable !== false ? false : String(row?.skuType || '').toUpperCase() === 'SFG' && toNum(th) === 0;
};

const PROCESS_COLORS = ['#fff7e6', '#eef7ee', '#eef3fd', '#fdf0f4', '#f3eefd', '#e8f7f5', '#f4f1ea', '#f0f4f8'];

export const buildProcessColorMap = (rows) => {
	const map = {};
	let i = 0;
	(rows || []).forEach((r) => {
		const p = r?.processId ?? r?.processName;
		if (p != null && p !== '' && map[p] === undefined) {
			map[p] = PROCESS_COLORS[i % PROCESS_COLORS.length];
			i += 1;
		}
	});
	return map;
};

export const FormulaPopover = ({ show, onToggle }) => (
	<div className='relative'>
		<button
			type='button'
			onClick={onToggle}
			className='flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 bg-white shadow-sm text-[12px] font-semibold text-gray-600 hover:bg-gray-50 whitespace-nowrap'
		>
			{/* Tailwind has no `brown` palette — use an explicit brown hex. */}
			<Info size={14} className='text-[#8B5E3C]' />
			How Final Production is calculated
			{show ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
		</button>
		{show && (
			<div className='absolute right-0 top-full mt-1.5 z-50 w-[560px] max-w-[80vw] max-h-[60vh] overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-xl px-4 py-3 flex flex-col gap-1 text-[12px] text-gray-500'>
				<span className='font-semibold text-gray-700'>FG (Finished Goods)</span>
				<span>
					<b>Day End Closing Stock</b> = Closing Stock + Planned Production − Total Dispatch
				</span>
				<span>
					<b>Final Production</b> = max(−Day End Closing Stock, 0)
				</span>
				<span>
					If Threshold is defined <b>and</b> Day End Closing Stock &lt; Threshold:
				</span>
				<span className='pl-3'>
					<b>Final Production</b> = max(Target − Day End Closing Stock, 0)
				</span>

				<span className='font-semibold text-gray-700 mt-2'>SFG (Semi-Finished Goods)</span>
				<span>
					<b>Day End Closing Stock</b> = Closing Stock + Planned Production − Planned Consumption
				</span>
				<span>
					If Threshold is defined <b>and</b> Day End Closing Stock ≤ Threshold:
				</span>
				<span className='pl-3'>
					<b>Final Production</b> = Target
				</span>

				<span className='italic mt-2'>
					Target = Max Stock (or Threshold, when Max Stock is not set).
				</span>
			</div>
		)}
	</div>
);

/* ---------------------- Footer buttons + chips (moved from the modal) ---------------------- */

const nextBtnClass = 'px-8 py-3 rounded-md bg-amber-400 text-black text-[15px] font-semibold hover:bg-amber-500 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed';
const backBtnClass = 'px-8 py-3 rounded-md border border-gray-300 text-gray-700 text-[15px] font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed';

export const buildFooterButtons = ({
	step, loading, stepOneValid, hasEdits, onBack, onNextStock, onRecalculate, onInitiate,
}) => [
	{
		id: 'back',
		side: 'left',
		label: '← Back',
		className: backBtnClass,
		show: step !== 1,
		onClick: onBack,
	},
	{
		id: 'nextStock',
		side: 'right',
		label: loading ? 'Loading…' : 'Next: Stock Configuration →',
		className: nextBtnClass,
		show: step === 1,
		disabled: !stepOneValid || loading,
		onClick: onNextStock,
	},
	// Step 2 shows Initiate by default; once the user edits any cell the button
	// becomes Recalculate (runs the recalc call, stays on step 2), and after a
	// successful recalculation Initiate comes back.
	{
		id: 'recalculate',
		side: 'right',
		label: loading ? 'Recalculating…' : 'Recalculate',
		className: nextBtnClass,
		show: step === 2 && hasEdits,
		disabled: loading,
		onClick: onRecalculate,
	},
	{
		id: 'initiate',
		side: 'right',
		label: 'Initiate',
		className: nextBtnClass,
		show: step === 2 && !hasEdits,
		disabled: loading,
		onClick: onInitiate,
	},
];

export const renderFooterButtons = (buttons) => {
	const group = (side) => (buttons || [])
		.filter((btn) => btn.side === side && btn.show)
		.map((btn) => (
			<button
				key={btn.id}
				type='button'
				onClick={btn.onClick}
				disabled={btn.disabled ?? false}
				className={btn.className}
			>
				{btn.label}
			</button>
		));
	return (
		<div className='flex justify-between items-center w-full gap-3'>
			<div className='flex items-center gap-3'>{group('left')}</div>
			<div className='flex items-center gap-3'>{group('right')}</div>
		</div>
	);
};

export const renderChips = (header) => {
	const chips = [
		header?.planName,
		header?.kitchen,
		header?.shift,
		header?.planningDate ? moment(header.planningDate).format('DD/MM/YYYY') : null,
	].filter(Boolean);
	return (
		<div className='flex flex-wrap gap-2'>
			{chips.map((c) => (
				<span key={c} className='px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-[12px] font-medium text-gray-700'>
					{c}
				</span>
			))}
		</div>
	);
};

export const InputCell = ({ row, colKey, onCommit }) => {
	const external = row?.[colKey] ?? '';
	const [value, setValue] = useState(external);

	useEffect(() => {
		setValue(external);
	}, [external]);

	const commit = () => {
		const next = value === '' ? '' : Number(value);
		if (next === external) return;
		onCommit?.(row?.id, colKey, next);
	};

	return (
		<div className='flex items-center gap-1.5'>
			<input
				type='number'
				min='0'
				value={value}
				onKeyDown={(e) => {
					if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
					if (e.key === 'Enter') e.target.blur();
				}}
				onChange={(e) => {
					const val = e.target.value;
					if (/[-+e]/i.test(val)) return;
					setValue(val);
				}}
				onBlur={commit}
				placeholder='0'
				className='w-[72px] border border-gray-200 rounded-lg px-2 py-1 text-[13px] text-center bg-white text-gray-800 transition-all placeholder:text-gray-300 hover:border-amber-300 focus:outline-none focus:border-amber-400 focus:shadow-[0_0_0_2px_rgba(251,191,36,0.15)]'
			/>
			{row?.unit && (
				<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>{row.unit}</span>
			)}
		</div>
	);
};

// Read-only qty cell — max 2 decimals with the unit suffix on numbers.
export const displayCell = (row, key) => {
	const v = row?.[key];
	if (v === null || v === undefined || v === '') return '-';
	if (typeof v !== 'number') return v;
	const num = Math.round(v * 100) / 100;
	return row?.unit ? `${num} ${row.unit}` : num;
};
// Default planning date: current date, but rolls over to the next day once the
// current time is past 11 AM (production for the rest of today is already locked).
export const getDefaultPlanningDate = () => {
	const now = moment();
	return (now.hour() >= 11 ? now.add(1, 'day') : now).toDate();
};

export const getInitialState = () => ({
	header: { planningDate: getDefaultPlanningDate() },
	search: '',
	localShiftList: [],
	confirmOpen: false,
	loading: false,
	showFormula: false,
	batchMap: {},
	step: 1,
	maxStep: 1,
	planId: null,
	tableData: [],
	stepTrack: {},
});

export const toDisplayRow = (r, variantMetaMap = {}, batchMap = {}) => {
	const meta = variantMetaMap[r?.variantId] || {};
	const row = {
		...r,
		id: r?.variantId,
		unit: meta.unit || '',
		originalUnit: meta.originalUnit || '',
		skuType: r?.skuType || meta.skuType || '',
		batchSize: r?.batchSize ?? batchMap[r?.variantId] ?? null,
	};
	if (meta.originalUnit) {
		QTY_KEYS.forEach((k) => {
			if (row[k] === null || row[k] === undefined) return;
			row[k] = toHigherValue(meta.originalUnit, row[k]);
		});
	}
	return row;
};
